/**
 * Creazione e configurazione di un registro (CF08/D08) — CL05.
 *
 * Lo stesso dialogo serve i due posti in cui un contatore nasce: le
 * impostazioni dell'organizzazione e la master del modello. Cambia solo
 * l'identità che gli si passa — organizzazione, o modello — perché il resto
 * (pattern, iniziale, reset annuale, stato) è la stessa cosa da chiedere.
 *
 * Tre cose che il dialogo **non** fa, deliberatamente:
 *
 * - **Non manda il `code` in modifica.** È l'identità su cui i codici emessi
 *   sono stati costruiti: cambiarlo li renderebbe irriconoscibili. In modifica
 *   si mostra, e basta.
 * - **Non lascia inviare con un pattern incoerente.** Le chiavi dei problemi
 *   arrivano dai validatori del contratto (`registryDraft`), non da una seconda
 *   validazione scritta qui che potrebbe essere più permissiva di quella vera.
 * - **Non promette niente con l'anteprima.** Il contratto la marca indicativa:
 *   il numero mostrato può essere già preso quando il processo parte davvero.
 */

import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useMemo, useState } from "react";
import { create_UUID, getTranslation } from "common";
import { useTranslations } from "hooks";
import { apiError, customFieldsApi } from "api/customFieldsApi";
import UserDataFieldsInfo from "components/UserDataFieldsInfo";
import { messageKeyFor } from "./errorMessages";
import { translateMessage } from "./messages";
import type {
  ImpactResult,
  RegistryConfiguration,
  RegistryPreview,
  RegistryRead,
  RegistryScope,
  SequencePattern,
} from "model/CustomFields";
import DialogHeader from "components/dialogs/DialogHeader";
import { PATTERN_LIMITS } from "components/custom-fields/sequences";
import {
  codeConflict,
  configurationOf,
  configurationWithOptionalSegments,
  createPayload,
  defaultConfiguration,
  draftIssues,
  effectiveName,
  nameConflict,
  updatePayload,
} from "./registryDraft";

/**
 * L'informativa di un campo del pattern.
 *
 * Una per campo, con lo stesso meccanismo già in uso per i campi del modello
 * (`UserDataFieldsInfo`): il titolo è la label del campo, il testo è il fratello
 * `.help` della stessa chiave — `sequence.prefix.label` /
 * `sequence.prefix.help`, come la spec documenta per l'annuale.
 */
const FieldInfo: React.FC<{ field: string; labelKey: string }> = ({
  field,
  labelKey,
}) => (
  <UserDataFieldsInfo
    labelKey={labelKey}
    bodyKey={`custom_fields.sequence.${field}.help`}
    triggerSx={{ ml: 0.5 }}
  />
);

interface RegistryEditorDialogProps {
  open: boolean;
  /** Organizzazione, e per un registro locale il modello a cui appartiene. */
  identity: RegistryScope;
  /**
   * Il codice dedotto per un progressivo locale appena creato.
   *
   * Arriva dal nome del campo in configurazione: quando c'è, il codice non si
   * scrive e non si cambia — è l'identità che il campo gli dà.
   */
  suggestedCode?: string;
  /** Il registro da modificare; assente quando se ne crea uno. */
  registry?: RegistryRead | null;
  /** `changed` dice al chiamante se ricaricare l'elenco. */
  onClose: (changed: boolean) => void;
}

const SEPARATORS: SequencePattern["separator"][] = ["-", "/", ".", "_", ""];
const YEARS: SequencePattern["year"][] = ["YYYY", "YY"];

/**
 * La larghezza dell'etichetta di un segmento (prefisso, postfisso, anno).
 *
 * Le tre tendine devono partire dallo stesso punto, e con l'etichetta libera
 * e' "Postfisso" a decidere dove: le altre due si accodano a un x diverso e la
 * colonna si sfalsa. Una misura sola, presa dalla voce piu' lunga, le tiene
 * allineate.
 */
const SEGMENT_LABEL_WIDTH = 140;

const RegistryEditorDialog: React.FC<RegistryEditorDialogProps> = ({
  open,
  identity,
  suggestedCode,
  registry = null,
  onClose,
}) => {
  const translations = useTranslations();
  const creating = registry === null;
  // Con un codice suggerito il campo è in sola lettura: il nome del campo lo
  // detta, e un codice diverso renderebbe il progressivo estraneo al campo.
  const codeLocked = creating && Boolean(suggestedCode?.trim());

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [configuration, setConfiguration] = useState<RegistryConfiguration>(
    defaultConfiguration()
  );
  const [enabledSegments, setEnabledSegments] = useState({
    prefix: false, postfix: false, year: false,
  });
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  /**
   * L'ultimo rifiuto, **intero**: codice e chiave.
   *
   * Tenere solo il codice costringeva a ricomporre la chiave a mano, e le
   * chiavi del catalogo sono minuscole mentre i codici sono maiuscoli: la
   * chiave composta non esisteva. La risoluzione la fa `messageKeyFor`.
   */
  const [error, setError] = useState<{
    code?: string | null;
    messageKey?: string | null;
  } | null>(null);
  const [preview, setPreview] = useState<RegistryPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  /**
   * Le anomalie della configurazione proposta, dopo l'analisi.
   *
   * Cambiare il pattern non è innocuo: può far collidere i codici che emetterà
   * con quelli già emessi. La spec chiede di vederlo **prima**, e il server lo
   * dice (`/impact`): qui si mostra e si chiede il consenso, invece di
   * scrivere e scoprirlo dal registro.
   */
  const [impact, setImpact] = useState<ImpactResult | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  /**
   * I registri dell'organizzazione, per il controllo sul nome.
   *
   * Si leggono solo in creazione: in modifica il nome non si tocca, quindi non
   * c'è niente da confrontare. Se la lettura fallisce l'elenco resta vuoto e si
   * salva lo stesso — il nome è una cortesia, e il vincolo vero, il codice unico
   * nell'organizzazione, lo difende il server.
   */
  const [registries, setRegistries] = useState<RegistryRead[]>([]);
  /**
   * L'utente ha scritto qualcosa nell'identità?
   *
   * `draftIssues` segnala subito il codice vuoto — è un problema vero, e tiene
   * spento il salvataggio. Ma mostrarlo **all'apertura** è un rimprovero per
   * qualcosa che non è ancora stato chiesto: l'alert aspetta il primo tocco.
   */
  const [codeTouched, setCodeTouched] = useState(false);

  // Il dialogo si riempie quando si apre: ricostruire la bozza a ogni render
  // scarterebbe quello che l'utente sta scrivendo.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setPreview(null);
    setImpact(null);
    setAcknowledged(false);
    setCodeTouched(false);
    if (registry) {
      setCode(registry.code);
      setName(registry.name);
      setConfiguration(configurationOf(registry));
      setEnabledSegments({
        prefix: !!registry.configuration.pattern.prefix,
        postfix: !!registry.configuration.pattern.postfix,
        year: registry.configuration.pattern.year !== "none",
      });
      setOptionsExpanded(Boolean(
        registry.configuration.pattern.prefix ||
        registry.configuration.pattern.postfix ||
        registry.configuration.pattern.year !== "none"
      ));
      setActive(registry.active);
    } else {
      // Il codice di un progressivo locale è il nome del campo: si mostra già
      // scritto, così quello che si salva è quello che si vede.
      setCode(suggestedCode?.trim().slice(0, PATTERN_LIMITS.code) ?? "");
      setName("");
      setConfiguration(defaultConfiguration());
      setEnabledSegments({ prefix: false, postfix: false, year: false });
      setOptionsExpanded(false);
      setActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, registry?.registryId]);

  const effectiveConfiguration = useMemo(
    () => configurationWithOptionalSegments(configuration, enabledSegments),
    [configuration, enabledSegments]
  );
  const issues = useMemo(
    () => draftIssues({ code, configuration: effectiveConfiguration, creating }),
    [code, effectiveConfiguration, creating]
  );

  /**
   * I problemi da mostrare.
   *
   * Diversi da quelli che tengono spento il salvataggio: il codice vuoto
   * disabilita il pulsante da subito, ma non si scrive addosso all'utente
   * appena apre la dialog. Appena scrive, la riga compare.
   */
  const visibleIssues =
    creating && !codeTouched
      ? issues.filter((key) => key !== "custom_fields.error.validation_failed")
      : issues;

  /**
   * Le sovrapposizioni con un progressivo che esiste già.
   *
   * Solo in creazione: in modifica né il codice né il nome si toccano. Sono due
   * controlli diversi perché due cose diverse possono collidere — il codice con
   * il codice, il nome con il nome — e ognuna si dice con parole sue.
   */
  const conflicts = useMemo(() => {
    if (!creating) return { code: null, name: null };
    return {
      code: codeConflict(code, registries),
      name: nameConflict(effectiveName({ code, name }), registries),
    };
  }, [creating, code, name, registries]);
  const hasConflict = Boolean(conflicts.code || conflicts.name);

  useEffect(() => {
    if (!open || !creating || !identity.organizationId) {
      setRegistries([]);
      return;
    }
    let annullato = false;
    (async () => {
      try {
        const page = await customFieldsApi.getRegistries(identity.organizationId);
        if (!annullato) setRegistries(page.items ?? []);
      } catch {
        // Vedi la nota su `registries`: senza elenco si salva, non si blocca.
        if (!annullato) setRegistries([]);
      }
    })();
    return () => {
      annullato = true;
    };
  }, [open, creating, identity.organizationId]);

  const setPattern = (changes: Partial<SequencePattern>) =>
    setConfiguration((prev) => ({ ...prev, pattern: { ...prev.pattern, ...changes } }));

  /** Il reset annuale senza anno nel pattern: si spegne, invece di spiegare un errore. */
  const annualResetDisabled = effectiveConfiguration.pattern.year === "none";

  /** C'è un'anomalia che nessun consenso può salvare. */
  const blockingAnomalies = (impact?.anomalies ?? []).some((a) => a.blocking);

  const handlePreview = async () => {
    if (!registry) return;
    setPreviewing(true);
    setError(null);
    try {
      setPreview(
        await customFieldsApi.previewRegistry(identity.organizationId, registry.registryId, {
          operationId: create_UUID(),
          expectedRevision: registry.revision,
        })
      );
    } catch (e) {
      setError(apiError(e) ?? { code: "VALIDATION_FAILED" });
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const input = {
        identity,
        registry,
        code,
        name,
        configuration: effectiveConfiguration,
        active,
        operationId: create_UUID(),
      };

      // Prima di cambiare il pattern su un registro che esiste, si guarda cosa
      // comporta: le anomalie si mostrano **qui**, e il consenso è esplicito.
      // Un registro appena creato non ha codici con cui collidere, quindi non
      // c'è niente da analizzare.
      if (!creating && !acknowledged) {
        const analisi = await customFieldsApi.analyzeRegistry(
          identity.organizationId,
          registry!.registryId,
          { ...updatePayload(input), operationId: input.operationId }
        );
        if (analisi.anomalies.length > 0) {
          setImpact(analisi);
          return;
        }
      }

      if (creating) {
        // Le sovrapposizioni sono scritte nel dialogo (`cf-registry-issues`) e
        // tengono spento il pulsante. Qui si rifiuta comunque: il pulsante non è
        // l'unico modo di arrivare al salvataggio, e un secondo avviso per la
        // stessa cosa sarebbe solo un doppione tardivo.
        if (hasConflict) return;
        await customFieldsApi.createRegistry(identity.organizationId, createPayload(input));
      } else {
        await customFieldsApi.updateRegistry(
          identity.organizationId,
          registry!.registryId,
          updatePayload(input)
        );
      }
      onClose(true);
    } catch (e) {
      // Il codice, non la frase: la frase è nel catalogo, e un conflitto di
      // revisione va riconosciuto per quello che è — qualcun altro ha scritto
      // nel frattempo, e la cosa giusta è rileggere.
      setError(apiError(e) ?? { code: "VALIDATION_FAILED" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      data-testid="cf-registry-editor"
      id="cf-registry-editor"
    >
      <DialogHeader sx={{ px: 4 }} onClose={() => onClose(false)}>
        {getTranslation(
          translations,
          creating ? "custom_fields.sequence.new" : "custom_fields.sequence.edit"
        )}
      </DialogHeader>
      <DialogContent>
        <Stack gap={2} pt={1}>
          {/* Punto 9: che non ci sia nessuna imposizione. Dirlo prima è più
              utile che lasciarlo dedurre da un asterisco. */}
          <Typography variant="body2" color="text.secondary">
            {getTranslation(translations, "custom_fields.sequence.optional_note")}
          </Typography>

          {creating ? (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <TextField
                size="small"
                sx={{ flex: 1 }}
                required
                disabled={codeLocked}
                label={getTranslation(translations, "custom_fields.sequence.code.label")}
                value={code}
                onChange={(e) => {
                  setCodeTouched(true);
                  setCode(e.target.value);
                }}
                inputProps={{ "data-testid": "cf-registry-code", maxLength: PATTERN_LIMITS.code }}
              />
              <FieldInfo field="code" labelKey="custom_fields.sequence.code.label" />
            </Stack>
          ) : (
            // In modifica l'identità si mostra e non si tocca: i codici già
            // emessi portano questo nome, e cambiarli li renderebbe estranei.
            <Typography variant="body2" color="text.secondary" data-testid="cf-registry-identity">
              {`${registry!.code} · ${registry!.name}`}
            </Typography>
          )}

          <Stack direction="row" gap={2} alignItems="center">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <TextField
                select
                size="small"
                // Senza larghezza minima la tendina si stringeva sul valore e
                // la label «Separatore» restava tagliata.
                sx={{ minWidth: 180 }}
                label={getTranslation(translations, "custom_fields.sequence.separator.label")}
                value={configuration.pattern.separator}
                onChange={(e) =>
                  setPattern({ separator: e.target.value as SequencePattern["separator"] })
                }
                SelectProps={{ "data-testid": "cf-registry-separator" } as never}
              >
                {SEPARATORS.map((separator) => (
                  <MenuItem key={separator || "none"} value={separator}>
                    {separator === ""
                      ? getTranslation(translations, "custom_fields.sequence.separator.none")
                      : separator}
                  </MenuItem>
                ))}
              </TextField>
              <FieldInfo field="separator" labelKey="custom_fields.sequence.separator.label" />
            </Stack>
          </Stack>

          <Stack direction="row" gap={2} alignItems="center">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <TextField
                size="small"
                type="number"
                label={getTranslation(translations, "custom_fields.sequence.padding.label")}
                value={configuration.pattern.padding}
                onChange={(e) => setPattern({ padding: Number(e.target.value) })}
                inputProps={{
                  "data-testid": "cf-registry-padding",
                  min: PATTERN_LIMITS.padding.min,
                  max: PATTERN_LIMITS.padding.max,
                }}
              />
              <FieldInfo field="padding" labelKey="custom_fields.sequence.padding.label" />
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <TextField
                size="small"
                type="number"
                disabled={!creating}
                label={getTranslation(translations, "custom_fields.sequence.initial.label")}
                value={configuration.initial}
                onChange={(e) =>
                  setConfiguration((prev) => ({ ...prev, initial: Number(e.target.value) }))
                }
                inputProps={{ "data-testid": "cf-registry-initial", min: 1 }}
              />
              <FieldInfo field="initial" labelKey="custom_fields.sequence.initial.label" />
            </Stack>
          </Stack>

          <Accordion expanded={optionsExpanded} onChange={(_, expanded) => setOptionsExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="cf-registry-options" data-testid="cf-registry-options-toggle">
              <Typography>
                {[
                  "custom_fields.sequence.prefix.label",
                  "custom_fields.sequence.postfix.label",
                  "custom_fields.sequence.year.label",
                ].map((key) => getTranslation(translations, key)).join(" · ")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails id="cf-registry-options">
              <Stack gap={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={<Checkbox checked={enabledSegments.prefix} onChange={(_, checked) =>
                      setEnabledSegments((prev) => ({ ...prev, prefix: checked }))
                    } inputProps={{ "data-testid": "cf-registry-prefix-enabled" } as never} />}
                    label={getTranslation(translations, "custom_fields.sequence.prefix.label")}
                    sx={{ minWidth: SEGMENT_LABEL_WIDTH }}
                  />
                  <TextField size="small" disabled={!enabledSegments.prefix}
                    label={getTranslation(translations, "custom_fields.sequence.prefix.label")}
                    value={configuration.pattern.prefix}
                    onChange={(e) => setPattern({ prefix: e.target.value })}
                    inputProps={{ "data-testid": "cf-registry-prefix", maxLength: PATTERN_LIMITS.prefix }} />
                  <FieldInfo field="prefix" labelKey="custom_fields.sequence.prefix.label" />
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={<Checkbox checked={enabledSegments.postfix} onChange={(_, checked) =>
                      setEnabledSegments((prev) => ({ ...prev, postfix: checked }))
                    } inputProps={{ "data-testid": "cf-registry-postfix-enabled" } as never} />}
                    label={getTranslation(translations, "custom_fields.sequence.postfix.label")}
                    sx={{ minWidth: SEGMENT_LABEL_WIDTH }}
                  />
                  <TextField size="small" disabled={!enabledSegments.postfix}
                    label={getTranslation(translations, "custom_fields.sequence.postfix.label")}
                    value={configuration.pattern.postfix}
                    onChange={(e) => setPattern({ postfix: e.target.value })}
                    inputProps={{ "data-testid": "cf-registry-postfix", maxLength: PATTERN_LIMITS.postfix }} />
                  <FieldInfo field="postfix" labelKey="custom_fields.sequence.postfix.label" />
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={<Checkbox checked={enabledSegments.year} onChange={(_, checked) => {
                      setEnabledSegments((prev) => ({ ...prev, year: checked }));
                      if (checked && configuration.pattern.year === "none") setPattern({ year: "YYYY" });
                      if (!checked) setConfiguration((prev) => ({ ...prev, annualReset: false }));
                    }} inputProps={{ "data-testid": "cf-registry-year-enabled" } as never} />}
                    label={getTranslation(translations, "custom_fields.sequence.year.label")}
                    sx={{ minWidth: SEGMENT_LABEL_WIDTH }}
                  />
                  <TextField select size="small" disabled={!enabledSegments.year}
                    label={getTranslation(translations, "custom_fields.sequence.year.label")}
                    value={configuration.pattern.year === "none" ? "YYYY" : configuration.pattern.year}
                    onChange={(e) => setPattern({ year: e.target.value as SequencePattern["year"] })}
                    SelectProps={{ "data-testid": "cf-registry-year" } as never}>
                    {YEARS.map((year) => (
                      <MenuItem key={year} value={year}>
                        {getTranslation(translations, `custom_fields.sequence.year.${year === "YYYY" ? "full" : "short"}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FieldInfo field="year" labelKey="custom_fields.sequence.year.label" />
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Senza anno nel pattern il reset non ha un momento in cui avvenire:
              la casella si **nasconde**, non si spegne. Spenta accanto a un
              pattern che non ha l'anno è una domanda senza risposta. */}
          {!annualResetDisabled && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={configuration.annualReset}
                  onChange={(e, checked) =>
                    setConfiguration((prev) => ({ ...prev, annualReset: checked }))
                  }
                  inputProps={{ "data-testid": "cf-registry-annual-reset" } as never}
                />
              }
              label={
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <span>
                    {getTranslation(translations, "custom_fields.sequence.annual_reset")}
                  </span>
                  <FieldInfo
                    field="annual_reset"
                    labelKey="custom_fields.sequence.annual_reset"
                  />
                </Stack>
              }
            />
          )}

          {!creating && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={active}
                  onChange={(e, checked) => setActive(checked)}
                  inputProps={{ "data-testid": "cf-registry-active" } as never}
                />
              }
              label={getTranslation(
                translations,
                active
                  ? "custom_fields.sequence.status.active"
                  : "custom_fields.sequence.status.inactive"
              )}
            />
          )}

          {(visibleIssues.length > 0 || hasConflict) && (
            <Alert severity="error" data-testid="cf-registry-issues">
              <Stack gap={0.5}>
                {visibleIssues.map((issue) => (
                  <Typography key={issue} variant="body2">
                    {getTranslation(translations, issue)}
                  </Typography>
                ))}
                {conflicts.code && (
                  <Typography variant="body2">
                    {getTranslation(translations, "custom_fields.sequence.code_conflict")}
                  </Typography>
                )}
                {conflicts.name && (
                  <Typography variant="body2">
                    {translateMessage(
                      getTranslation(translations, "custom_fields.sequence.name_conflict"),
                      { name: conflicts.name.name }
                    )}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          {error && (
            <Alert severity="error" data-testid="cf-registry-error-code">
              {getTranslation(translations, messageKeyFor(error))}
            </Alert>
          )}

          {impact && (
            <Alert
              severity={blockingAnomalies ? "error" : "warning"}
              data-testid="cf-registry-impact"
            >
              <Stack gap={0.5}>
                <Typography variant="body2">
                  {getTranslation(translations, "custom_fields.impact.title")}
                </Typography>
                {impact.anomalies.map((anomaly) => (
                  <Typography key={anomaly.id} variant="body2">
                    {getTranslation(translations, anomaly.messageKey)}
                  </Typography>
                ))}
                {/* Un'anomalia bloccante non si accetta: il consenso si chiede
                    solo per quello che si può accettare sapendo. */}
                {blockingAnomalies ? (
                  <Typography variant="body2" data-testid="cf-registry-impact-blocking">
                    {getTranslation(translations, "custom_fields.impact.blocking")}
                  </Typography>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={acknowledged}
                        onChange={(e, checked) => setAcknowledged(checked)}
                        inputProps={{ "data-testid": "cf-registry-impact-ack" } as never}
                      />
                    }
                    label={getTranslation(translations, "custom_fields.impact.confirm")}
                  />
                )}
              </Stack>
            </Alert>
          )}

          {!creating && (
            <Stack direction="row" alignItems="center" gap={1}>
              <LoadingButton
                size="small"
                variant="outlined"
                loading={previewing}
                onClick={handlePreview}
                data-testid="cf-registry-preview"
              >
                {getTranslation(translations, "custom_fields.sequence.preview")}
              </LoadingButton>
              {preview && (
                <Box data-testid="cf-registry-preview-codes">
                  <Typography variant="caption" display="block">
                    {preview.sampleCodes.join(", ")}
                  </Typography>
                  {/* Indicativa: non riserva niente, e chi la legge deve saperlo. */}
                  <Typography variant="caption" color="text.secondary">
                    {getTranslation(
                      translations,
                      "custom_fields.sequence.preview.advisory"
                    )}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} data-testid="cf-registry-cancel">
          {getTranslation(translations, "custom_fields.action.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          // Un pattern che il contratto rifiuta non si manda: il server
          // risponderebbe 422 e l'utente imparerebbe dopo. Un'anomalia vista e
          // non accettata nemmeno: il consenso è la seconda metà della
          // decisione, e senza di esso il pulsante chiederebbe la prima.
          disabled={
            issues.length > 0 ||
            hasConflict ||
            blockingAnomalies ||
            (impact !== null && !acknowledged)
          }
          onClick={handleSave}
          data-testid="cf-registry-save"
        >
          {getTranslation(translations, "pm.button.save")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RegistryEditorDialog;
